/**
 * Semantic event tracking service.
 *
 * Buffers events in memory and flushes as batched POSTs to /api/events/.
 * Fire-and-forget — never blocks UI or surfaces errors.
 */

import { tokenManager } from '../lib/api'

const API_URL = import.meta.env.VITE_API_URL || ''
const FLUSH_INTERVAL_MS = 3_000
const FLUSH_THRESHOLD = 10

interface TrackingEvent {
  event_type: string
  properties: Record<string, unknown>
  page_url: string
  timestamp: string
  session_id: string
}

// --- Module-level state ---

function getSessionId(): string {
  let id = sessionStorage.getItem('tracking_session_id')
  if (!id) {
    id = crypto.randomUUID()
    sessionStorage.setItem('tracking_session_id', id)
  }
  return id
}

const sessionId = getSessionId()
let buffer: TrackingEvent[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null

// --- Flush logic ---

function flush(): void {
  if (buffer.length === 0) return
  const events = buffer
  buffer = []
  if (flushTimer) {
    clearTimeout(flushTimer)
    flushTimer = null
  }

  const token = tokenManager.getAccessToken()
  if (!token) return

  fetch(`${API_URL}/api/events/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ events }),
  }).catch(() => {})
}

function flushBeacon(): void {
  if (buffer.length === 0) return
  const events = buffer
  buffer = []
  if (flushTimer) {
    clearTimeout(flushTimer)
    flushTimer = null
  }

  const token = tokenManager.getAccessToken()
  if (!token) return

  fetch(`${API_URL}/api/events/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ events }),
    keepalive: true,
  }).catch(() => {})
}

// Flush on page hide / visibility change
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') flushBeacon()
})
window.addEventListener('pagehide', flushBeacon)

// --- Public API ---

export function trackEvent(
  eventType: string,
  properties: Record<string, unknown> = {},
): void {
  buffer.push({
    event_type: eventType,
    properties,
    page_url: window.location.pathname,
    timestamp: new Date().toISOString(),
    session_id: sessionId,
  })

  if (buffer.length >= FLUSH_THRESHOLD) {
    flush()
  } else if (!flushTimer) {
    flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS)
  }
}
